
import { useState } from 'react';

interface NewsletterSignupProps {
  className?: string;
}

export default function NewsletterSignup({ className = '' }: NewsletterSignupProps) {
  const [formData, setFormData] = useState({
    email: '',
    country: '',
    fundingOption: '',
    firstName: '',
    lastName: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Australia',
    'Japan', 'South Korea', 'Singapore', 'Netherlands', 'Sweden', 'Norway',
    'Denmark', 'Switzerland', 'Austria', 'Belgium', 'Italy', 'Spain', 'Other'
  ];

  const fundingOptions = [
    { id: 'early-bird-single', name: 'Early Bird - Single Pack ($15)', price: 15 },
    { id: 'starter-pack', name: 'Starter Pack ($35)', price: 35 },
    { id: 'complete-care', name: 'Complete Care Bundle ($65)', price: 65 },
    { id: 'champion-supporter', name: 'Champion Supporter ($120)', price: 120 },
    { id: 'ocean-guardian', name: 'Ocean Guardian ($250)', price: 250 },
    { id: 'updates-only', name: 'Just updates (no funding)', price: 0 }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    if (!formData.country) {
      setError('Please select your country');
      return;
    }

    if (!formData.fundingOption) {
      setError('Please select a funding option');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const submitData = new FormData();
      submitData.append('email', formData.email);
      submitData.append('first_name', formData.firstName);
      submitData.append('last_name', formData.lastName);
      submitData.append('country', formData.country);
      submitData.append('funding_option', formData.fundingOption);
      submitData.append('signup_date', new Date().toISOString());
      
      // Find selected funding option details
      const selectedOption = fundingOptions.find(option => option.id === formData.fundingOption);
      if (selectedOption) {
        submitData.append('funding_amount', selectedOption.price.toString());
        submitData.append('funding_name', selectedOption.name);
      }

      const response = await fetch('https://readdy.ai/api/form/d46g3jrq5e24j8uscqg0', {
        method: 'POST',
        body: submitData,
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          email: '',
          country: '',
          fundingOption: '',
          firstName: '',
          lastName: ''
        });
      } else {
        setError('Failed to submit. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section className="py-20 bg-teal-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-teal-200 rounded-lg p-8 text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-3xl text-teal-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Thank You for Your Support!</h3>
            <p className="text-lg text-gray-600 mb-6">
              You've successfully joined our Kickstarter campaign. We'll notify you as soon as the campaign launches 
              and keep you updated on our progress.
            </p>
            <div className="bg-teal-50 p-4 rounded-lg">
              <p className="text-teal-800 font-semibold">
                🌊 Together, we're creating a revolution in sustainable period care!
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="newsletter-signup" className="py-20 bg-teal-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-gray-200 rounded-lg p-8">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Join Our Kickstarter Campaign
            </h3>
            <p className="text-lg text-gray-600">
              Be the first to know when our campaign launches and secure your Marine Pad rewards. 
              Select your preferred funding option below.
            </p>
          </div>

          <form onSubmit={handleSubmit} data-readdy-form id="kickstarter-signup">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="mb-6">
              <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                Country *
              </label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm pr-8"
                required
                disabled={isSubmitting}
              >
                <option value="">Select your country</option>
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label htmlFor="fundingOption" className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Funding Option *
              </label>
              <select
                id="fundingOption"
                name="fundingOption"
                value={formData.fundingOption}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm pr-8"
                required
                disabled={isSubmitting}
              >
                <option value="">Select your funding preference</option>
                {fundingOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div className="mb-6 text-red-600 text-sm flex items-center">
                <i className="ri-error-warning-line mr-2"></i>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-teal-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-lg"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Joining Campaign...
                </span>
              ) : (
                'Join Kickstarter Campaign'
              )}
            </button>

            <p className="text-xs text-gray-500 mt-4 text-center">
              We respect your privacy. This information will only be used to notify you about our Kickstarter campaign 
              and deliver your rewards. Unsubscribe at any time.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
